import React, { useState, useEffect } from "react";
import { motion, useAnimationControls } from "framer-motion";
import { Link } from "react-router-dom";
import PictureImg from "../../helpers/PictureImg";

// ---------------------------------------------------------------------------
// Product Section — Figma node 117:610
//
// Cards use ABSOLUTE POSITIONING per Dev Mode spec (NOT flex + padding):
//   1) onoff hero (167:9037)    : 1440x640 — full-bleed photo + 10% scrim
//      • card-text : absolute left:60 top:60 w:557 (badge + title + desc)
//      • cta       : absolute left:60 top:552
//
//   2) retail half (167:9120)   :  708x640 — paper bg + pink ellipse
//      • card-text : absolute left:60 top:60 w:557 (title + desc + cta)
//      • mockup    : absolute top:262 h:378 w:100% (flush to bottom)
//
//   3) food half (185:9289)     :  708x640 — paper bg + warm ellipse
//      • same layout as retail, different ellipse position
//
//   4) crm wide (185:9363)      : 1440x320 — paper bg + gray ellipse
//      • card-text : absolute left:60 top:50% (translateY -50%) w:557
//      • mockup    : absolute left:617 top:0 w:823 h:100% (right-bleed)
// ---------------------------------------------------------------------------

// 우진 (2026-04-30): 각 카드 자세히 보기 → 해당 제품 상세 페이지로 라우팅.
//   onoff      → /products/membership
//   pos-retail → /products/retail-pos
//   pos-food   → /products/food-pos
//   crm        → /products/crm
// 우진 (2026-05-06): 필드명 `key` → `id`로 변경. {...PRODUCT_CARDS[i]}로
// spread하면 React가 `key`를 특수 prop으로 해석해서 경고 발생하기 때문.
const PRODUCT_CARDS = [
  {
    id: "onoff",
    variant: "hero",
    image: "/img/landing/product-omni.png",
    badge: "가장 많이 도입하는 솔루션",
    title: "온·오프라인 멤버십 통합",
    desc: "고객은 매장과 자사몰에서 동일한 적립금, 쿠폰, 등급 혜택을 받습니다. 어느 채널에서 결제하든 일관된 구매 경험이 이어집니다.",
    cta: "자세히 보기",
    href: "/products/membership",
  },
  {
    id: "pos-retail",
    variant: "half",
    image: "/img/landing/product-retail.png",
    title: "리테일 POS",
    desc: "스캔하는 순간 매장과 자사몰 재고가 동시에 움직입니다. 재고 관리부터 품절 방지까지 한 화면에서.",
    cta: "자세히 보기",
    href: "/products/retail-pos",
  },
  {
    id: "pos-food",
    variant: "half",
    image: "/img/landing/product-food.png",
    title: "외식 POS",
    desc: "매장 주문, 키오스크, 테이블 오더, 배달까지 모든 주문이 모입니다. 어떤 주문도 놓치지 않고, 접수부터 응대까지 처리할 수 있습니다.",
    cta: "자세히 보기",
    href: "/products/food-pos",
  },
  {
    id: "crm",
    variant: "wide",
    image: "/img/landing/product-crm.png",
    title: "CRM",
    desc: "주문 데이터를 바탕으로 고객을 세그먼트하고, 맞춤 캠페인을 발송합니다. 캠페인별 성과는 리포트로 확인하고 다음 캠페인에 바로 반영할 수 있습니다.",
    cta: "자세히 보기",
    href: "/products/crm",
  },
];

// Icon colors per Figma Icon/* tokens. `color` is the stroke/fill color;
// `bg` is the 15% alpha tint behind the icon.
const ETC_CARDS = [
  { icon: "table", color: "indigo", title: "테이블오더", desc: "고객이 직접 테이블에서 주문" },
  { icon: "kiosk", color: "blue",   title: "키오스크",  desc: "무인 주문으로 인건비 절감" },
  { icon: "queue", color: "green",  title: "웨이팅",    desc: "대기 관리 자동화" },
  { icon: "kds",   color: "orange", title: "KDS",      desc: "주방 디스플레이" },
  { icon: "point", color: "cyan",   title: "포인트 모드", desc: "듀얼스크린 화면" },
];

export default function Product() {
  return (
    <section className="product">
      {/* Decorative blob 은 .product 의 background-image 로 이동. 별도 div 불필요. */}
      <div className="product__contents">
        {/* product-main (120:809) — flex-col gap:60 → title + Frame 326 */}
        <div className="product__main">
          <header className="product__header">
            <h2 className="product__title">
              비즈니스에 필요한 모든 것들,<br />
              <span className="product__title-accent">그로잉세일즈에서.</span>
            </h2>
          </header>

          {/* Frame 326 (120:808) — flex-col gap:24 → hero + row + wide */}
          <div className="product__grid">
            <ProductCard {...PRODUCT_CARDS[0]} cardKey={PRODUCT_CARDS[0].id} />
            <div className="product__row">
              <ProductCard {...PRODUCT_CARDS[1]} cardKey={PRODUCT_CARDS[1].id} />
              <ProductCard {...PRODUCT_CARDS[2]} cardKey={PRODUCT_CARDS[2].id} />
            </div>
            <ProductCard {...PRODUCT_CARDS[3]} cardKey={PRODUCT_CARDS[3].id} />
          </div>
        </div>

        {/* product-etc (195:14791) — sits directly on product's white bg */}
        <ProductEtc cards={ETC_CARDS} />
      </div>
    </section>
  );
}

// Single card renderer. All variants use absolute positioning. The only
// structural difference between hero vs half/wide is WHERE the CTA lives:
//   • hero      → CTA is its own absolutely-positioned child (left:60 top:552)
//   • half/wide → CTA is the last child of the .product__card-text flex block
function ProductCard({ cardKey, variant = "half", image, badge, title, desc, cta, href }) {
  const isHero = variant === "hero";
  // Variant + per-card modifiers (drive SCSS for blob position, colors etc.)
  const variantClass = `product__card--${variant}`;
  const keyClass = cardKey ? `product__card--${cardKey}` : "";
  return (
    <article
      className={`product__card ${variantClass} ${keyClass}`.trim()}
    >
      {isHero ? (
        // Hero: full-bleed photo + 10% scrim
        <>
          {image && (
            <div className="product__card-bg" aria-hidden="true">
              <PictureImg
                src={image}
                alt=""
                loading="lazy"
                onError={(e) => { e.target.style.display = "none"; }}
              />
            </div>
          )}
          <div className="product__card-scrim" aria-hidden="true" />
        </>
      ) : (
        // Half / wide: decorative blob behind everything
        <div className="product__card-blob" aria-hidden="true" />
      )}

      {/* Text block — absolute at left:60 top:60 w:557 (or centered for wide) */}
      <div className="product__card-text">
        {isHero && badge && <span className="product__card-badge">{badge}</span>}
        <div className="product__card-text-inner">
          <h3 className="product__card-title">{title}</h3>
          <p className="product__card-desc">{desc}</p>
        </div>
        {!isHero && <CardCta label={cta} href={href} />}
      </div>

      {/* Mockup — every export (retail/food/crm) ships with the device or
          browser mock baked into the PNG itself, so we only need to slot
          the image into the card region. No chrome bar, no border, no
          fade — those are part of the image now. */}
      {!isHero && image && (
        <div className="product__card-mockup" aria-hidden="true">
          <div className="product__card-mockup-frame">
            <PictureImg
              src={image}
              alt=""
              loading="lazy"
              onError={(e) => { e.target.style.display = "none"; }}
            />
          </div>
        </div>
      )}

      {/* Hero CTA — absolutely positioned at the bottom-left */}
      {isHero && <CardCta label={cta} href={href} />}
    </article>
  );
}

// 우진 (2026-04-30): 카드 CTA 가 모두 내부 페이지(/products/*) 라 react-router
// Link 로 처리. SPA 라우팅 유지.
function CardCta({ label, href }) {
  return (
    <Link to={href || "#"} className="product__card-cta">
      {label}
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M9 18l6-6-6-6" />
      </svg>
    </Link>
  );
}

// product-etc (195:14791) — Figma:
//   • list  : 1440×267, cards absolute at left:0 top:0 (flex gap:24)
//   • card  : 342×* fixed, bg #F4F5F8, rounded-24, px:40 py:56, flex-col gap:24
//   • icon  : 64×64 rounded-12 with per-card tinted bg (color @ 15% alpha)
//   • dots  : active 24×8 pill, default 8×8 circle
//   • arrow : 54×54 circles, gap 16
// Carousel advances by PAGE_SIZE cards per page using translateX.
//
// Responsive:
//   ≥ 768px : 4 per page (desktop default)
//   480–767 : 2 per page
//   < 480   : 1 per page
// SCSS adjusts `--etc-card-w` / `--etc-card-gap` at the same breakpoints so the
// translateX calc stays consistent with the visible card width.
function getPageSize(w) {
  if (w < 480) return 1;
  if (w < 768) return 2;
  return 4;
}

function ProductEtc({ cards }) {
  const [pageSize, setPageSize] = useState(() =>
    typeof window === "undefined" ? 4 : getPageSize(window.innerWidth)
  );
  // (2026-05-06) page 단위 (4 카드씩) → index 단위 (1 카드씩) 로 변경.
  // 화살표 클릭 1번에 카드 한 장만 슬라이드되며, 좌/우 가장자리는 viewport
  // 까지 bleed 되어 다음/이전 카드가 살짝 비치도록 구성.
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const update = () => setPageSize(getPageSize(window.innerWidth));
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  // 마지막 유효 index — 이 값이 곧 마지막 카드를 한 번에 모두 보여주는 지점.
  // 5 cards / pageSize 4 → lastIndex = 1 (positions: 0=cards 0-3, 1=cards 1-4)
  const lastIndex = Math.max(0, cards.length - pageSize);
  const totalDots = lastIndex + 1;

  // pageSize 변동 시 index 가 새로운 lastIndex 를 초과하지 않도록 clamp.
  useEffect(() => {
    if (index > lastIndex) setIndex(lastIndex);
  }, [lastIndex, index]);

  // 우진 (2026-05-06): 손가락/커서 스와이프 지원.
  // useAnimationControls 로 트랙 x 를 명시적으로 제어 — index 가 변하지 않는
  // (충분히 안 끌어당긴) 드래그도 onDragEnd 에서 제자리로 스냅 가능.
  const trackControls = useAnimationControls();

  // index 1 증가 = 카드 1장 + gap 1번만큼 좌측으로 이동.
  const computeTargetX = (i) =>
    `calc(${i} * -1 * (var(--etc-card-w) + var(--etc-card-gap)))`;

  useEffect(() => {
    trackControls.start({
      x: computeTargetX(index),
      transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, pageSize]);

  const handleDragEnd = (_e, info) => {
    const offset = info.offset.x;
    const velocity = info.velocity.x;
    const OFFSET_THRESHOLD = 50;
    const VELOCITY_THRESHOLD = 500;

    let nextIndex = index;
    if (offset < -OFFSET_THRESHOLD || velocity < -VELOCITY_THRESHOLD) {
      nextIndex = Math.min(lastIndex, index + 1);
    } else if (offset > OFFSET_THRESHOLD || velocity > VELOCITY_THRESHOLD) {
      nextIndex = Math.max(0, index - 1);
    }

    if (nextIndex !== index) {
      setIndex(nextIndex); // useEffect 가 애니메이션 트리거
    } else {
      // 드래그가 임계치 미달 → 원래 index 로 부드럽게 복귀
      trackControls.start({
        x: computeTargetX(index),
        transition: { duration: 0.3, ease: [0.16, 1, 0.3, 1] },
      });
    }
  };

  return (
    <div className="product__etc">
      <header className="product__etc-header">
        <h3 className="product__etc-title">매장 운영을 조금 더 완벽하게.</h3>
        {/* 우진 (2026-04-30): 부가서비스 목록 페이지로 라우팅. */}
        <Link to="/extra-service" className="product__etc-more">
          자세히 보기
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </Link>
      </header>

      <div className="product__etc-list">
        <motion.div
          className="product__etc-track"
          drag={lastIndex > 0 ? "x" : false}
          dragMomentum={false}
          onDragEnd={handleDragEnd}
          animate={trackControls}
        >
          {cards.map((c, i) => (
            <article
              className={`product__etc-card product__etc-card--${c.color}`}
              key={i}
            >
              <div className="product__etc-icon">
                <EtcIcon name={c.icon} />
              </div>
              <div className="product__etc-card-text">
                <h4 className="product__etc-card-title">{c.title}</h4>
                <p className="product__etc-card-desc">{c.desc}</p>
              </div>
            </article>
          ))}
        </motion.div>
      </div>

      <div className="product__etc-controls">
        <div className="product__etc-dots" role="tablist" aria-label="product etc pagination">
          {Array.from({ length: totalDots }).map((_, i) => (
            <button
              key={i}
              type="button"
              role="tab"
              aria-selected={i === index}
              aria-label={`position ${i + 1}`}
              className={`product__etc-dot ${i === index ? "product__etc-dot--active" : ""}`}
              onClick={() => setIndex(i)}
            />
          ))}
        </div>
        <div className="product__etc-arrows">
          <button
            className="product__etc-arrow"
            onClick={() => setIndex((i) => Math.max(0, i - 1))}
            disabled={index === 0}
            aria-label="이전"
            type="button"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <button
            className="product__etc-arrow"
            onClick={() => setIndex((i) => Math.min(lastIndex, i + 1))}
            disabled={index === lastIndex}
            aria-label="다음"
            type="button"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

function EtcIcon({ name }) {
  const common = {
    width: 32,
    height: 32,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round",
    strokeLinejoin: "round",
  };
  const map = {
    // 테이블오더 — 모니터/디스플레이에 체크
    table: (
      <svg {...common}>
        <rect x="2.5" y="4" width="19" height="13" rx="2" />
        <path d="M8 21h8M12 17v4" />
        <path d="M8.5 10.5l2.5 2.5 4.5-4.5" />
      </svg>
    ),
    // 키오스크 — 세로 태블릿에 UI 표시
    kiosk: (
      <svg {...common}>
        <rect x="5" y="2" width="14" height="20" rx="2" />
        <path d="M8.5 6.5h7M8.5 10h5" />
        <rect x="8.5" y="13.5" width="7" height="5" rx="1" />
      </svg>
    ),
    // 웨이팅 — 시계
    queue: (
      <svg {...common}>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3.5 2" />
      </svg>
    ),
    // KDS — 주방 디스플레이 + 셰프모자 실루엣 느낌의 아이콘
    kds: (
      <svg {...common}>
        <rect x="2.5" y="5" width="19" height="12" rx="2" />
        <path d="M7 21h10M12 17v4" />
        <path d="M8 9.5h8M8 13h5" />
      </svg>
    ),
    // 포인트 모드 — 듀얼스크린(겹쳐진 화면) + 포인트 마크
    point: (
      <svg {...common}>
        <rect x="2.5" y="4" width="14" height="10" rx="1.5" />
        <rect x="7.5" y="10" width="14" height="10" rx="1.5" />
        <path d="M13 15.5l1.25 2.5 2.75.4-2 1.9.5 2.7" strokeWidth="0" />
      </svg>
    ),
  };
  return map[name] || null;
}
